import APP_PATHS from '../../constants/app-paths';
import CAPABILITIES from '../../constants/capabilities';
import HOOK_DATA_STATUS from '../../constants/hook-data-status';
import LabUser from '../../models/users/lab-user';
import PRODUCT_IDENTIFIER_TYPES from '../../constants/cms/on-demand-courses/product-identifier-types';
import React from 'react';
import USER_TYPES from '../../constants/user-type.js';
import logError from '../../utilities/logging/log-error';
import makeRequest from '@llt-frontend/make-request';
import useClientSubscriptions from './use-client-subscriptions';
import { useData } from '@llt-frontend/react-fetch';
import { useHistory } from 'react-router-dom';
import useIncomingAssessmentId from '../routing/use-incoming-assessment-id';
import useStudentCourses from './use-student-courses';

//

const API_HOST = process.env.REACT_APP_API_HOST || '';
const STUDENT_API_PATH = `${API_HOST}/v1/learn-lab/users/me/students`;
const STUDENTS_API_PATH = `${API_HOST}/v1/learn-lab/users/students`;
const USER_ENDPOINT = `${API_HOST}/v1/users/me?include_entity=true&include_students=true`;

// Exports
// -------

export default function useLabUser() {
  const hasIncomingAssessment = Boolean(useIncomingAssessmentId());

  const history = useHistory();

  const { data: labUser, ...results } = useData(USER_ENDPOINT, {
    queryKey: ['lab-user'],
    queryOptions: {
      onSuccess({ capabilities, id, students } = {}) {
        if (
          status === HOOK_DATA_STATUS.pending ||
          !id ||
          !capabilities.includes(CAPABILITIES.labAccess)
        ) {
          return;
        }

        const hasStudents = Boolean(students?.length);
        const hasActiveStudent = Boolean(
          hasStudents &&
            activeStudentId &&
            // We need to ensure we're validating that any cached, active student belongs to the
            // current user. We have other safety around this (e.g., API auth requirements), but
            // best to be thorough.
            students?.find((student) => student.uuid === activeStudentId)
        );

        // If the user can't switch students try to select the first student available so we can
        // send them straight to the lab
        if (!capabilities.includes(CAPABILITIES.profileManagement)) {
          if (!hasStudents) {
            logError(
              '[User State Error]: User has no students and cannot add one due to role restrictions'
            );
          }

          if (students?.[0]?.uuid === window.localStorage.getItem('selected_student_id')) {
            return;
          }

          updateActiveStudentId(students?.[0]?.uuid);
          return;
        }

        if (!hasActiveStudent) {
          const autoSelectStudent = students?.length === 1 && !hasIncomingAssessment;
          updateActiveStudentId(autoSelectStudent ? students[0]?.uuid : undefined);
        }
      },
      select: LabUser,
    },
    requestOptions: {
      maxAttempts: 1,
      onError() {
        // Do nothing, the app will respond defensively to missing user data
      },
    },
  });

  const [activeStudentId, setActiveStudentId] = React.useState(
    localStorage.getItem('selected_student_id')
  );
  const [activeUser, setActiveUser] = React.useState(labUser);
  const [status, setStatus] = React.useState(HOOK_DATA_STATUS.idle);

  const { hasSubscription, loading: clientSubscriptionsLoading } = useClientSubscriptions({
    clientUid: labUser?.type === USER_TYPES.client ? labUser?.id : undefined,
  });

  const { data: studentCourses, loading: studentCoursesLoading } = useStudentCourses({
    activeStudentId,
    userUuid: labUser?.uuid,
  });

  const loading =
    results.loading ||
    clientSubscriptionsLoading ||
    studentCoursesLoading ||
    status === HOOK_DATA_STATUS.pending;

  //
  // Methods

  const updateActiveStudentId = React.useCallback((id) => {
    window.localStorage.setItem('selected_student_id', id);
    setActiveStudentId(id);

    if (id) {
      makeRequest(`${STUDENTS_API_PATH}/${id}`, {
        body: {
          active: true,
        },
        method: 'PUT',
      });
    }
  }, []);

  //
  // Side-Effects

  React.useEffect(
    function _handleLabUserData() {
      setActiveUser(labUser);
    },
    [labUser]
  );

  //
  // Result

  return React.useMemo(() => {
    const activeStudent = activeUser?.students?.find((student) => student.uuid === activeStudentId);
    const userPresent = !results.loading && Boolean(activeUser?.id);

    //

    function __hasCapability(capability) {
      return activeUser?.capabilities.includes(capability);
    }

    function __hasContentAccess(content, type) {
      for (const { identifier, productType } of content.relatedProductIdentifiers) {
        switch (productType) {
          case PRODUCT_IDENTIFIER_TYPES.catalogItemId: {
            if (
              studentCourses?.find(
                (course) =>
                  identifier && [course?.item_id, course?.item_data?.parent_id].includes(identifier)
              )
            ) {
              return true;
            }
            break;
          }

          case PRODUCT_IDENTIFIER_TYPES.enablement: {
            const enablementSearch = type ? { tag: identifier, type } : identifier;
            if (activeStudent.hasEnablement(enablementSearch)) {
              return true;
            }
            break;
          }

          case PRODUCT_IDENTIFIER_TYPES.subscription: {
            if (__hasSubscription(identifier)) {
              return true;
            }
            break;
          }

          case PRODUCT_IDENTIFIER_TYPES.urlToken: {
            if (__hasUrlAccess(identifier)) {
              return true;
            }
            break;
          }

          default:
          // Do nothing
        }
      }

      return false;
    }

    function __hasSubscription(category) {
      return hasSubscription({ category, studentId: activeStudentId });
    }

    function __hasUrlAccess(accessToken) {
      return accessToken === new URLSearchParams(window.location.search).get('access_token');
    }

    //

    return {
      ...results,

      get activeStudentId() {
        return activeStudent?.uuid;
      },

      get authenticated() {
        return userPresent && __hasCapability(CAPABILITIES.labAccess);
      },

      loading,

      get students() {
        return activeUser?.students;
      },

      get unsupportedRole() {
        return userPresent && !__hasCapability(CAPABILITIES.labAccess);
      },

      activeStudent,

      activeUser,

      async createProfile({ firstName, lastName }, makeNewStudentActive = true) {
        if (!__hasCapability(CAPABILITIES.profileManagement)) {
          return false;
        }

        setStatus(HOOK_DATA_STATUS.pending);
        const student = await makeRequest(STUDENT_API_PATH, {
          body: {
            first_name: firstName,
            last_name: lastName,
          },
          method: 'POST',
        });

        if (!student?.uuid) {
          // Unrecoverable error
          logError(
            `[Unexpected Data]: Unable to create profile during ${
              makeNewStudentActive ? 'manual' : 'auto'
            } add`
          );
          history.push(APP_PATHS.oops);
          return;
        }

        await results.refetch();

        if (makeNewStudentActive) {
          updateActiveStudentId(student.uuid);
        }

        setStatus(HOOK_DATA_STATUS.complete);
        return true;
      },

      hasCapability: __hasCapability,

      hasContentAccess: __hasContentAccess,

      hasSubscription: __hasSubscription,

      setActiveStudentId(id) {
        updateActiveStudentId(id);
      },

      setPendingUser(pendingUser) {
        // This should only be used when a user can "login", but isn't authorized (e.g., learning
        // tools and lead users). If we already have access to a user provided by the user service
        // this is a no-op

        if (activeUser) {
          logError(
            '[Unexpected Event]: Error attempting to override active user with pending user data'
          );
          return;
        }

        setActiveUser(pendingUser);
      },
    };
  }, [
    activeUser,
    activeStudentId,
    hasSubscription,
    history,
    loading,
    results,
    studentCourses,
    updateActiveStudentId,
  ]);
}
